import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { 
  CssBaseline, 
  Box, 
  AppBar, 
  Toolbar, 
  Typography, 
  Container, 
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';

// Page components
import Dashboard from './pages/Dashboard';
import TutorDashboard from './pages/TutorDashboard';
import Documents from './pages/Documents';
import Segments from './pages/Segments';
import GenerateExam from './pages/GenerateExam';
import GenerateExamV2 from './pages/GenerateExamV2';
// ReviewAnswers removed - functionality moved to CompleteReview
import CompleteReview from './pages/CompleteReview';
import CompleteReviewList from './pages/CompleteReviewList';
import CourseTopicManagement from './pages/CourseTopicManagement';
import Login from './pages/Login';
import Register from './pages/Register';
import FirstLogin from './pages/FirstLogin';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import ResendVerification from './pages/ResendVerification';
import ReferralLanding from './pages/ReferralLanding';
import LandingPageB2B from './pages/LandingB2B/LandingPageB2B';
import StudentLandingPage from './pages/LandingStudent/StudentLandingPage';
import DemoViewerPage from './pages/DemoViewer/DemoViewerPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Contact from './pages/Contact';
import Navigation from './components/Navigation';
import ProtectedRoute from './components/ProtectedRoute';
import EmailVerificationBanner from './components/EmailVerificationBanner';
import AnalyticsWrapper from './components/AnalyticsWrapper';
import FeedbackWidget from './components/FeedbackWidget';
import StudentRedirect from './components/StudentRedirect';

// Subscription components
import SubscriptionPlans from './components/SubscriptionPlans';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import SubscriptionManage from './pages/SubscriptionManage';

// Context providers
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';

// API utilities
import { configureAuthToken } from './utils/api';

// Analytics
import analytics from './services/analytics';
import AnalyticsDebugPanel from './components/AnalyticsDebugPanel';

// Main App Component with Router
function AppWithRouter() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { darkMode, toggleDarkMode } = useTheme();
  const { token, isAuthenticated, needsFirstLogin, needsEmailVerification, loading } = useAuth();

  // Initialize analytics on app mount
  useEffect(() => {
    analytics.init();
  }, []);
  
  // Configure API token when it changes
  useEffect(() => {
    configureAuthToken(token);
  }, [token]);
  
  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Show loading screen while checking authentication
  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        height="100vh"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <CssBaseline />
      
      {/* Top navigation - only show if authenticated and not in first-login flow */}
      {isAuthenticated() && !needsFirstLogin() && (
        <AppBar position="sticky">
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
              examr
            </Typography>
            <Tooltip title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}>
              <IconButton color="inherit" onClick={toggleDarkMode} edge="end">
                {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>
      )}
      
      {/* Main content */}
      <Box sx={{ display: 'flex', flex: 1 }}>
        {/* Side navigation - only show if authenticated and not in first-login flow */}
        {isAuthenticated() && !needsFirstLogin() && (
          <Navigation open={drawerOpen} onClose={() => setDrawerOpen(false)} />
        )}
        
        {/* Main content area */}
        <Container 
          component="main" 
          sx={{ 
            flexGrow: 1, 
            p: (isAuthenticated() && !needsFirstLogin()) ? 3 : 0, 
            mt: (isAuthenticated() && !needsFirstLogin()) ? 2 : 0 
          }}
        >
          {/* Email verification banner for students */}
          {isAuthenticated() && !needsFirstLogin() && needsEmailVerification() && (
            <EmailVerificationBanner />
          )}
          
          <AnalyticsWrapper>
            <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/resend-verification" element={<ResendVerification />} />
            <Route path="/join/alpha/:referralToken" element={<ReferralLanding />} />
            
            {/* First-login route - requires authentication but allows first-login status */}
            <Route 
              path="/first-login" 
              element={
                <ProtectedRoute allowFirstLogin={true}>
                  <FirstLogin />
                </ProtectedRoute>
              } 
            />
            
            {/* Student landing page for unauthenticated users, protected dashboard for authenticated */}
            <Route 
              path="/" 
              element={
                isAuthenticated() ? (
                  <ProtectedRoute>
                    <StudentRedirect />
                  </ProtectedRoute>
                ) : (
                  <StudentLandingPage />
                )
              } 
            />
            
            {/* Demo viewer routes - public access */}
            <Route path="/demo/exam/:examId" element={<DemoViewerPage />} />
            <Route path="/demo/quiz/:examId" element={<DemoViewerPage />} />
            
            {/* Educator/B2B landing page - redirect to student landing */}
            <Route path="/educators" element={<Navigate to="/" replace />} />
            <Route path="/business" element={<Navigate to="/" replace />} />
            
            {/* Privacy Policy - public access */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            
            {/* Contact Page - public access */}
            <Route path="/contact" element={<Contact />} />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute requiredRole="TUTOR">
                  <TutorDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/documents" 
              element={
                <ProtectedRoute requiredRole="TUTOR">
                  <Documents />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/segments/:documentId?" 
              element={
                <ProtectedRoute requiredRole="TUTOR">
                  <Segments />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/generate" 
              element={
                <ProtectedRoute>
                  <GenerateExam />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/generate-v2" 
              element={
                <ProtectedRoute>
                  <GenerateExamV2 />
                </ProtectedRoute>
              } 
            />
            {/* Review route removed - functionality merged into CourseTopicManagement */}
            {/* ReviewAnswers route removed - use CompleteReview instead */}
            <Route 
              path="/complete-review" 
              element={
                <ProtectedRoute requiredRole="TUTOR">
                  <CompleteReviewList />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/complete-review/:documentId" 
              element={
                <ProtectedRoute requiredRole="TUTOR">
                  <CompleteReview />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/courses" 
              element={
                <ProtectedRoute requiredRole="TUTOR">
                  <CourseTopicManagement />
                </ProtectedRoute>
              } 
            />
            
            {/* Subscription routes */}
            <Route 
              path="/subscription/plans" 
              element={
                <ProtectedRoute>
                  <SubscriptionPlans />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/subscription/success" 
              element={
                <ProtectedRoute>
                  <SubscriptionSuccess />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/subscription/manage" 
              element={
                <ProtectedRoute>
                  <SubscriptionManage />
                </ProtectedRoute>
              } 
            />
            
            {/* Default redirect based on authentication and first-login status */}
            <Route 
              path="*" 
              element={
                !isAuthenticated() ? 
                  <Navigate to="/login" replace /> :
                  needsFirstLogin() ?
                    <Navigate to="/first-login" replace /> :
                    <Navigate to="/" replace />
              } 
            />
            </Routes>
          </AnalyticsWrapper>
        </Container>
      </Box>
      
      {/* Analytics Debug Panel - Only in development */}
      {process.env.NODE_ENV === 'development' && <AnalyticsDebugPanel />}
      
      {/* Feedback Widget - Show only when authenticated and not in first-login */}
      {isAuthenticated() && !needsFirstLogin() && <FeedbackWidget />}
    </Box>
  );
}

// Wrap the app with providers
function App() {
  return (
    <ThemeProvider>
      <Router>
        <AuthProvider>
          <AppWithRouter />
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App; 